import { Component, OnInit, PLATFORM_ID, inject, resource, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { GoogleAuthService } from '../services/google-auth-service';
import { ThemeService } from '../services/theme.service';
import { UpdateService } from '../services/update.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FacebookBrowserDialog } from './facebook-browser-dialog';
import { ContactDialog } from './contact-dialog';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../../../apiclients/playble';
import { MatBadge } from '@angular/material/badge';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';

@Component({
  selector: 'app-shell',
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    RouterOutlet,
    RouterLink,
    MatTooltip,
    MatDialogModule,
    MatBadge,
    MatSidenavModule,
    MatListModule,
  ],
  templateUrl: './shell.html',
  styleUrl: './shell.css'
})
export class Shell implements OnInit {

  auth = inject(GoogleAuthService);
  router = inject(Router);
  themeService = inject(ThemeService);
  platformId = inject(PLATFORM_ID);
  dialog = inject(MatDialog);
  userService = inject(UserService);
  updateService = inject(UpdateService);

  facebookInAppBrowser = signal(false);
  facebookWarningDismissed = signal(false);

  adminQueueResource = resource({
    params: () => this.auth.currentUser()?.uid ?? null,
    loader: async ({ params }) => {
      if (!params) {
        return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
      }

      try {
        const adminStatus = await firstValueFrom(this.userService.apiUserIsAdminGet());
        if (!adminStatus?.isAdmin) {
          return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
        }

        return {
          isAdmin: true,
          pendingCount: adminStatus.pendingCount ?? 0,
          pendingPlaygroundCount: adminStatus.pendingPlaygroundCount ?? 0,
          pendingPlaygroupCount: adminStatus.pendingPlaygroupCount ?? 0,
          pendingDayTripCount: adminStatus.pendingDayTripCount ?? 0,
        };
      } catch {
        return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
      }
    }
  });

  async ngOnInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const hasDismissed = localStorage.getItem('facebookWarningDismissed');
    if (hasDismissed === 'true') {
      this.facebookWarningDismissed.set(true);
    }

    const userAgent = navigator.userAgent ?? '';
    if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(userAgent)) {
      this.facebookInAppBrowser.set(true);
      if (!this.facebookWarningDismissed()) {
        const dialogRef = this.dialog.open(FacebookBrowserDialog, { autoFocus: false });
        await firstValueFrom(dialogRef.afterClosed());
        this.dismissFacebookWarning();
      }
    }

    this.updateService.checkForUpdate();
  }

  dismissFacebookWarning(): void {
    this.facebookWarningDismissed.set(true);
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.setItem('facebookWarningDismissed', 'true');
  }

  openContactDialog(event?: Event): void {
    event?.preventDefault();
    this.dialog.open(ContactDialog, {
      autoFocus: false,
      width: '560px',
      maxWidth: '95vw'
    });
  }

  isSignupRoute(): boolean {
    return this.router.url.startsWith('/signup');
  }

  isChromeHiddenRoute(): boolean {
    return this.isSignupRoute() || this.router.url.startsWith('/day-trips/new');
  }
}
